import { get, compact, size } from "lodash"
import { createSelector } from "reselect"
import { Creators as ResourceCreators } from "modules/resources/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { Creators as DialogCreators } from "modules/dialog/model"
import { PERMISSION_LEVELS, hasPermission } from "helpers/permissions"
import { getIsIntradayAccountEnabled } from "modules/account-root/selectors"
import { getModels } from "./model"
import { getPortfolio } from "./portfolios"
import { getATForecasts } from "modules/resources/selectors"
import { getFavorites } from "selectors/favorites"
import { matchATForecastByName } from "../helpers"

import Confirm from "components/confirm-dialog"
import EditPortfolio from "modules/edit-portfolio"
import saveRecentResources from "hocs/saveRecentResources"

const getAccount = state => state.global.selected.account
const getPermissionLevel = state => get(state, "global.data.resourcePermission.data.resource.permissionLevel")

const getIsFavorite = createSelector(
  [getPortfolio, getFavorites],
  (portfolio, favorites) => get(favorites, portfolio.id, false),
)

export const getIsLinkedToAlphaTheory = createSelector(
  [getPortfolio, getATForecasts],
  (portfolio, forecasts) => {
    const { name } = portfolio
    return matchATForecastByName(name, forecasts)
  },
)

const getPortfolioWithMeta = createSelector(
  [getPortfolio, getIsFavorite, getPermissionLevel, getIsLinkedToAlphaTheory],
  (portfolio, isFavorite, permissionLevel, linkedToAlphaTheory) => ({
    ...portfolio,
    alias: portfolio.shortId,
    isFavorite,
    permissionLevel,
    linkedToAlphaTheory,
  }),
)

export const getContextualMenuItems = createSelector(
  [getAccount, getPortfolioWithMeta, getModels, getIsIntradayAccountEnabled],
  getPortfolioMenuItems,
)

export function getPortfolioMenuItems(account, portfolio, models, intradayAccountEnabled, isOpPro, callbacks = {}) {
  const { onSuccess, onDelete } = callbacks
  const { toggleFavorite, updatePortfolio, deletePortfolio } = ResourceCreators
  const { open: openDialog, close: closeDialog } = DialogCreators
  const { add: addNotification } = NotificationCreators
  const { id, alias, name, isFavorite, permissionLevel, isLinkedToAlphaTheory } = portfolio

  const hasModels = size(models)
  const editable = hasPermission(permissionLevel, PERMISSION_LEVELS.WRITE) && hasModels
  const deletable = hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER)

  const EditPortfolioWithRecentResources = saveRecentResources(EditPortfolio, "portfolioId")

  // We don't have access to the redux dispatch here but we can have the action caller pass it as a parameter
  return compact([
    editable && {
      id: "edit",
      label: "Edit",
      action: (dispatch) => dispatch(openDialog({
        title: "Edit portfolio",
        // [TODO] [#184018795] JSX shouldn't be in selectors or models
        content: <EditPortfolioWithRecentResources
          portfolioId={get(portfolio, "id")}
          linkedToAlphaTheory={isLinkedToAlphaTheory}
          savePortfolio={(data) => {
            dispatch(updatePortfolio(portfolio, data, onSuccess))
          }}
        />,
      })),
    },
    {
      id: "toggleFavorite",
      label: isFavorite ? "Remove from favorites" : "Add to favorites",
      action: (dispatch) => {
        dispatch(toggleFavorite(id, "portfolio", !isFavorite))
        dispatch(addNotification("info", null, `${name} successfully ${isFavorite ? "removed from" : "added to"} favorites`))
      },
    },
    editable && {
      id: "upload",
      label: "Upload positions",
      to: `/${account}/p/${alias}/upload`,
    },
    {
      id: "permissions",
      label: "View permissions",
      to: `/${account}/p/${alias}/permissions/${isOpPro ? "applications" : "members"}`,
    },
    deletable && {
      id: "delete",
      label: "Delete",
      className: "danger",
      action: (dispatch, navigate) => {
        dispatch(openDialog({
          title: `Delete portfolio: ${name}`,
          content: <Confirm
            text={`Are you sure you want to delete ${name}? All associated position data will be deleted.`}
            keyword={name}
            cta="Delete portfolio"
            action={() => {
              dispatch(deletePortfolio(id, onDelete))
              if (!onDelete) {
                navigate(`/${account}/home/resources`)
              }
            }}
            onClose={() => dispatch(closeDialog())}
          />,
        }))
      },
    },
  ])
}
